import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import PopularExperiences from './components/PopularExperiences';
import CitySitingTours from './components/CitySitingTours';
import HowItWorks from './components/HowItWorks';
import CustomizeExperience from './components/CustomizeExperience';
import BecomeGuide from './components/BecomeGuide';
import Footer from './components/Footer';
import AboutUs from './pages/AboutUs';
import CookiePolicy from './pages/CookiePolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Careers from './pages/Careers';
import Partnership from './pages/Partnership';
import Sitemap from './pages/Sitemap';
import UgandaTrips from './pages/UgandaTrips';
import CookieConsent from './components/CookieConsent';
import LoadingScreen from './components/LoadingScreen';
import ExperienceDetail from './pages/ExperienceDetail';
import AllExperiences from './pages/AllExperiences';
import UgandaCityTours from './pages/UgandaCityTours';
import GuideApplication from './pages/GuideApplication';
import GuideProfile from './pages/GuideProfile';
import Success from './pages/Success';
import Uganda from './pages/Uganda';
import SelfDriveSafaris from './pages/SelfDriveSafaris';
import SafariPackages from './pages/SafariPackages';
import CulturalTours from './pages/CulturalTours';
import Blog from './pages/Blog';
import CustomizeAdventure from './pages/CustomizeAdventure';
import ChatBot from './components/ChatBot';
import TravelGuides from './pages/TravelGuides';
import InteractiveMap from './components/InteractiveMap';
import NewsletterSubscription from './components/NewsletterSubscription';
import PartnershipsSection from './components/PartnershipsSection';
import TrustBadges from './components/TrustBadges';
import GorillaTrekking from './pages/GorillaTrekking';
import ChimpanzeeTracking from './pages/ChimpanzeeTracking';
import GiveawaySection from './components/GiveawaySection';
import NotFound from './pages/NotFound';
import Resources from './pages/Resources';
import WriteForUs from './pages/WriteForUs';
import UgandaNightlifeGuide from './pages/UgandaNightlifeGuide';
import NightCrawlingSection from './components/NightCrawlingSection';
import SelfDriveSafariSection from './components/SelfDriveSafariSection';
import HireACarSection from './components/HireACarSection';

// Import blog post pages
import GorillaTrekkingGuide from './pages/blog/GorillaTrekkingGuide';
import HiddenGemsKampala from './pages/blog/HiddenGemsKampala';
import SafariPhotographyGuide from './pages/blog/SafariPhotographyGuide';
import BestTimeVisitUganda from './pages/blog/BestTimeVisitUganda';
import UgandaTribesExperiences from './pages/blog/UgandaTribesExperiences';
import MustVisitPlacesKampala from './pages/blog/MustVisitPlacesKampala';
import SelfDriveSafariTips from './pages/blog/SelfDriveSafariTips';
import KampalaFoodGuide from './pages/blog/KampalaFoodGuide';
import KampalaNightlife from './pages/blog/KampalaNightlife';
import JinjaNightCrawling from './pages/blog/JinjaNightCrawling';
import JinjaNightlife from './pages/blog/JinjaNightlife';

// Import city tour pages
import KampalaWalkingTour from './pages/tours/KampalaWalkingTour';
import KampalaComplete from './pages/tours/KampalaComplete';
import KampalaFood from './pages/tours/KampalaFood';
import JinjaRafting from './pages/tours/JinjaRafting';
import JinjaBungee from './pages/tours/JinjaBungee';
import JinjaSunset from './pages/tours/JinjaSunset';
import JinjaKayaking from './pages/tours/JinjaKayaking';
import JinjaQuadBiking from './pages/tours/JinjaQuadBiking';
import EntebbeDay from './pages/tours/EntebbeDay';

// Import safari package pages
import UgandasFinest from './pages/safaris/UgandasFinest';
import PrimateExpress from './pages/safaris/PrimateExpress';
import GrandCircuit from './pages/safaris/GrandCircuit';

// Content sections for each category
const CategorySections = {
  safaris: () => (
    <>
      <PopularExperiences />
      <CustomizeExperience />
      <GiveawaySection />
      <InteractiveMap />
    </>
  ),
  cityTours: () => (
    <>
      <CitySitingTours />
    </>
  ),
  selfDrive: () => (
    <>
      <SelfDriveSafariSection />
      <HireACarSection />
      <HowItWorks />
    </>
  ),
  nightCrawlers: () => (
    <>
      <NightCrawlingSection />
    </>
  )
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<'safaris' | 'cityTours' | 'selfDrive' | 'nightCrawlers'>('safaris');

  useEffect(() => {
    // Simulate loading time - shorter duration
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <>
            <Navbar />
            <Hero onCategoryChange={setActiveCategory} />
            
            {/* Dynamic content based on selected category */}
            {CategorySections[activeCategory]()}
            
            <BecomeGuide />
            <NewsletterSubscription />
            <PartnershipsSection />
            <TrustBadges />
            <Footer />
          </>
        } />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/partnership" element={<Partnership />} />
        <Route path="/sitemap" element={<Sitemap />} />
        <Route path="/uganda-trips" element={<UgandaTrips />} />
        <Route path="/all-experiences" element={<AllExperiences />} />
        <Route path="/uganda-city-tours" element={<UgandaCityTours />} />
        <Route path="/guide-application" element={<GuideApplication />} />
        <Route path="/guides/:id" element={<GuideProfile />} />
        <Route path="/success" element={<Success />} />
        <Route path="/uganda" element={<Uganda />} />
        <Route path="/self-drive-safaris" element={<SelfDriveSafaris />} />
        <Route path="/safari-packages" element={<SafariPackages />} />
        <Route path="/cultural-tours" element={<CulturalTours />} />
        <Route path="/gorilla-trekking" element={<GorillaTrekking />} />
        <Route path="/chimpanzee-tracking" element={<ChimpanzeeTracking />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/customize-adventure" element={<CustomizeAdventure />} />
        <Route path="/travel-guides" element={<TravelGuides />} />
        <Route path="/write-for-us" element={<WriteForUs />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/Uganda_Nightlife_Ultimate_Guide" element={<UgandaNightlifeGuide />} />
        
        {/* Blog Post Routes */}
        <Route path="/blog/ultimate-guide-gorilla-trekking" element={<GorillaTrekkingGuide />} />
        <Route path="/blog/hidden-gems-kampala" element={<HiddenGemsKampala />} />
        <Route path="/blog/safari-photography-guide" element={<SafariPhotographyGuide />} />
        <Route path="/blog/best-time-visit-uganda" element={<BestTimeVisitUganda />} />
        <Route path="/blog/uganda-tribes-cultural-experiences" element={<UgandaTribesExperiences />} />
        <Route path="/blog/must-visit-places-kampala" element={<MustVisitPlacesKampala />} />
        <Route path="/blog/self-drive-safari-tips" element={<SelfDriveSafariTips />} />
        <Route path="/blog/kampala-food-guide" element={<KampalaFoodGuide />} />
        <Route path="/blog/kampala-nightlife-guide" element={<KampalaNightlife />} />
        <Route path="/blog/jinja-night-crawling" element={<JinjaNightCrawling />} />
        <Route path="/blog/jinja-nightlife" element={<JinjaNightlife />} />
        
        {/* City Tour Routes */}
        <Route path="/tours/kampala-walking" element={<KampalaWalkingTour />} />
        <Route path="/tours/kampala-complete" element={<KampalaComplete />} />
        <Route path="/tours/kampala-food" element={<KampalaFood />} />
        <Route path="/tours/jinja-rafting" element={<JinjaRafting />} />
        <Route path="/tours/jinja-bungee" element={<JinjaBungee />} />
        <Route path="/tours/jinja-sunset" element={<JinjaSunset />} />
        <Route path="/tours/jinja-kayaking" element={<JinjaKayaking />} />
        <Route path="/tours/jinja-quad-biking" element={<JinjaQuadBiking />} />
        <Route path="/tours/entebbe-day" element={<EntebbeDay />} />
        
        {/* Safari Package Routes */}
        <Route path="/self-drive-safaris/ugandas-finest" element={<UgandasFinest />} />
        <Route path="/self-drive-safaris/primate-express" element={<PrimateExpress />} />
        <Route path="/self-drive-safaris/grand-circuit" element={<GrandCircuit />} />
        
        <Route path="/experiences/:id" element={<ExperienceDetail />} />
        
        {/* 404 Route - Must be last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <ChatBot />
      <CookieConsent />
    </Router>
  );
}

export default App;